import React, { useState, useReducer, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Radio,
} from "antd";
import { v4 as uuidv4 } from "uuid";

import {
  FETCH_ALL_COLOR_API,
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_DEPT_API,
} from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";

import { createShippingWithMutation } from "../../../Services/ShippingService";
import MutateCreationForm from "../../elements/Forms/MutateCreationForm/MutateCreationForm";
import { createStock } from "../../../Services/StockService";
import StockMutateCreationForm from "../../elements/Forms/StockMutateCreationForm/StockMutateCreationForm";

const MutateCreationPage = ({ userData }) => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [selectedSender, setSelectedSender] = useState(
    userData?.counterCode === 'ECOMMRI' ? 'ECOMMRI' : userData?.counterCode
  );
  const [submitting, setSubmitting] = useState(false);

  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [barcodeValue, setBarcodeValue] = useState(""); // Store barcode value
  const [scannerActive, setScannerActive] = useState(false); // Track if the scanner is active

  const { fetchedData: deptFetched = {} } = useFetch(
    `${FETCH_ALL_DEPT_API}/active`,
    state
  );
  const deptList = deptFetched?.data || [];

  // fetch all counters
  const { fetchedData: countersFetched = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    state
  );
  const counters = countersFetched?.data || [];

  // build filtered counters (only where counter.dept is in active dept names)
  const counterData = useMemo(() => {
    if (!Array.isArray(deptList) || deptList.length === 0) return counters; // fallback before dept data arrives
    const activeDeptNames = new Set(
      deptList.map((d) => d?.name).filter(Boolean)
    );
    return counters.filter((c) => c?.dept && activeDeptNames.has(c.dept));
  }, [counters, deptList]);


  const {
    fetchedData: { data: ColorData },
    callReFetch: callReFetchColor,
    loading: loadingColor,
  } = useFetch(`${FETCH_ALL_COLOR_API}`, state);

  const [selectedStock, setSelectedStock] = useState(null);

  // This function is now handled directly in StockCreationForm
  const handleScanBarcode = (err, result) => {
    // The actual implementation is now in StockCreationForm
    if (result) {
      const scannedBarcode = result.text;
      setBarcodeValue(scannedBarcode);
    }
  };

  // Handle stock selection from StockCreationForm
  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
  };

  useEffect(() => {
    // Generate unique noForm using 10-digit Unix timestamp (seconds)
    const timestamp = Math.floor(Date.now() / 1000);
    form.setFieldsValue({ noForm: `RM${timestamp}` });

    const totalQuantity = items.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );
    const totalCost = items.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
      0
    );
    form.setFieldsValue({ totalQuantity, totalCost });
  }, [items, form]);

  // Handle Add or Edit Item
  const handleAddOrEditItem = async () => {
    try {
      const values = await modalForm.validateFields();
      const item = { ...values };

      setItems((prevItems) => {
        if (editingItemIndex === null) {
          // Check if item with same barcode already exists
          const existingItemIndex = prevItems.findIndex(
            existingItem => existingItem.barcode === item.barcode
          );
          
          if (existingItemIndex !== -1) {
            // Aggregate quantities for existing barcode
            const updatedItems = [...prevItems];
            const existingItem = updatedItems[existingItemIndex];
            updatedItems[existingItemIndex] = {
              ...existingItem,
              quantity: Number(existingItem.quantity) + Number(item.quantity)
            };
            message.success(`Quantity updated for barcode ${item.barcode}. New total: ${updatedItems[existingItemIndex].quantity}`);
            return updatedItems;
          } else {
            // Add new item
            return [...prevItems, item];
          }
        } else {
          // Edit existing item
          const updatedItems = [...prevItems];
          updatedItems[editingItemIndex] = item;
          return updatedItems;
        }
      });

      modalForm.resetFields();
      setIsModalVisible(false);
      setEditingItemIndex(null);
    } catch (errorInfo) {
      console.error("Validation Failed:", errorInfo);
    }
  };

  const handleEditItem = (index) => {
    setEditingItemIndex(index);
    modalForm.setFieldsValue(items[index]);
    setIsModalVisible(true);
  };

  const handleDeleteItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    message.success("Item deleted successfully");
  };

  const handleFormSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();


      if (items.length === 0) {
        message.error("You must add at least one item!");
        return;
      }

      const destinationCode = String(
        values.destinationCode || values.destination || ""
      ).trim();
      if (!destinationCode) {
        message.error("Please select a destination counter.");
        return;
      }

      const shippingData = {
        noForm: values.noForm,
        noSJ: values.noSJ,
        sender: values.sender,
        destination: destinationCode,
        keterangan: values.keterangan,
        totalCost: values.totalCost,
        totalQuantity: values.totalQuantity,
        status: "in delivery",
        tanggal_kirim: values.tanggal_kirim,
        tanggal_terima_eta: values.tanggal_terima_eta,
      };
      // Ensure all numeric values are properly converted and validated
      const stockData = items.map((item) => {
        // Parse numeric values with fallbacks to prevent NaN
        const size = Number(item.size);
        const quantity = Number(item.quantity);
        const price = Number(item.price);

        return {
          noSJ: values.noSJ,
          article: item.article || "",
          color: item.color || "",
          size: isNaN(size) ? 0 : size,
          quantity: isNaN(quantity) ? 0 : quantity,
          currentQuantity: isNaN(quantity) ? 0 : quantity, // Explicitly set currentQuantity
          assort: item.assort || "",
          price: isNaN(price) ? 0 : price,
          barcode: item.barcode,
          counterCode: destinationCode,
          currCounterCode: values.sender,
          status: "in delivery",
        };
      });
      await createShippingWithMutation(shippingData, stockData);

      message.success("Shipping details submitted successfully!");
      form.resetFields();
      setItems([]);
    } catch (error) {
      console.error("Error submitting shipping details:", error);
      message.error(
        error?.response?.data?.message ||
          "Failed to submit shipping details. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Article", dataIndex: "article", key: "article" },
    { title: "Warna", dataIndex: "color", key: "color" },
    { title: "Size", dataIndex: "size", key: "size" },
    { title: "Barcode", dataIndex: "barcode", key: "barcode" },
    { title: "Assort", dataIndex: "assort", key: "assort" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    { title: "Harga", dataIndex: "price", key: "price" },
    {
      title: "Actions",
      key: "actions",
      render: (_, __, index) => (
        <div>
          <Button
            onClick={() => handleEditItem(index)}
            style={{ marginRight: 10 }}
          >
            Edit
          </Button>
          <Button onClick={() => handleDeleteItem(index)} danger>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px",
      }}
    >
      <h2 style={{ textAlign: "center" }}>Buat Mutasi / Return</h2>
      <div style={{ width: "100%", maxWidth: "1200px" }}>
        <MutateCreationForm
          form={form}
          userData={userData}
          counterData={counterData}
          handleFormSubmit={handleFormSubmit}
          onSenderChange={setSelectedSender}
          disableSender={items.length > 0 || userData?.counterCode === 'ECOMMRI'} // Disable if items exist OR if ECOMMRI user
        />

        <Table
          columns={columns}
          dataSource={items}
          rowKey={(record, index) => index}
          pagination={false}
          scroll={{ x: "max-content" }}
        />

        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <Button
            type="primary"
            onClick={() => {
              if (loadingColor) {
                message.info("Please wait, loading color data...");
                return;
              }
              setIsModalVisible(true);
            }}
          >
            Tambahkan Barang
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            loading={submitting}
            disabled={submitting}
            style={{ marginLeft: 10 }}
          >
            Submit
          </Button>
        </div>

        <Modal
          title={editingItemIndex === null ? "Tambahkan Barang" : "Edit Item"}
          visible={isModalVisible}
          onOk={handleAddOrEditItem}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingItemIndex(null);
          }}
          okText={editingItemIndex === null ? "Add" : "Update"}
        >
          <StockMutateCreationForm
            modalForm={modalForm}
            ColorData={ColorData}
            userData={userData}
            onScanBarcode={handleScanBarcode}
            setBarcodeValue={setBarcodeValue}
            setScannerActive={setScannerActive}
            scannerActive={scannerActive}
            onStockSelect={handleStockSelect}
            colorLoading={loadingColor}
            senderCounterCode={selectedSender}
          />
        </Modal>
      </div>
    </div>
  );
};

export default MutateCreationPage;
